import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  SelectChangeEvent,
  Button,
  Divider,
} from '@mui/material';
import { ChangeEvent, FC, useEffect } from 'react';
import { CaretRight, CaretLeft } from '@phosphor-icons/react';
import { useSelector, useDispatch } from 'react-redux';
import { ValidationError } from 'yup';
import { useNavigate } from 'react-router';

import { RootState } from '../../utils/redux/redux';
import {
  updateAssistantDefinition,
  setStepStatus,
  setErrors,
  clearErrors,
} from '../../utils/redux/slices/userJourneySlice';
import { assistantDefinitionSchema } from '../../schemas/userJourney/assistantDefination';
import { useTimeZones } from '../../hooks/UseTimeZones';
import { useQuery } from '@tanstack/react-query';
import { getAllVoices } from '../../utils/apis';
import { useAgents } from '../../hooks/useAgents';
import theme from '../../theme';

interface AssistantDefinationProps {
  selectedSubSection?: string;
  onNext: () => void;
  onPrev: () => void;
  isPending: boolean;
  shouldShowMenuConfigFlow?: boolean;
  isGeneralSupportFlow?: boolean;
}

const AssistantDefination: FC<AssistantDefinationProps> = ({
  selectedSubSection,
  onNext,
  onPrev,
  isPending,
  shouldShowMenuConfigFlow,
  isGeneralSupportFlow = false,
}) => {
  const dispatch = useDispatch();
  const { assistantDefinition, errors } = useSelector(
    (state: RootState) => state.userJourney
  );

  const { agents } = useAgents();

  const { timeZones } = useTimeZones();

  const { data: sampleVoices } = useQuery({
    queryKey: ['ultraVoices'],
    queryFn: () => getAllVoices({ service: 'Ultra' }),
  });

  const navigate = useNavigate();

  useEffect(() => {
    if (agents?.agents?.length > 0) {
      const currentAgent = agents.agents[agents.agents.length - 1];
      dispatch(
        updateAssistantDefinition({
          assistantName: assistantDefinition.data.assistantName,
          timezone: assistantDefinition.data.timezone,
          voice: assistantDefinition.data.voice.name
            ? assistantDefinition.data.voice
            : '',
          language: assistantDefinition.data.language || '',
          personality:
            currentAgent?.config?.personality ||
            assistantDefinition.data.personality,
          agentChannel: currentAgent?.agentChannel || '',
        })
      );

      // Only set step as complete if all required fields are filled
      const isComplete = Boolean(
        currentAgent.agentName &&
          currentAgent?.config?.timeZone &&
          (currentAgent?.agentChannel === 'whatsapp' || // Skip voice check for WhatsApp
            currentAgent?.config?.voice?.name ||
            currentAgent?.config?.voice?.voiceName) &&
          currentAgent?.config?.language?.name &&
          currentAgent?.config?.personality
      );

      dispatch(
        setStepStatus({
          step: 'assistantDefinition',
          status: { isComplete: isComplete, hasError: false },
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agents, dispatch]);

  console.log(sampleVoices?.data?.data);

  // Update the default language and voice selection logic
  useEffect(() => {
    if (
      sampleVoices?.data?.data?.length > 0 &&
      assistantDefinition.data.language === ''
    ) {
      const uniqueLanguages = getUniqueLanguages();
      if (uniqueLanguages.length > 0) {
        // Default to English (US)
        const defaultLanguage = 'English (US)';
        dispatch(updateAssistantDefinition({ language: defaultLanguage }));

        // Find Cassidy-English voice specifically for English (US)
        const cassidyVoice = sampleVoices?.data?.data?.find(
          (voice: any) =>
            voice.language === 'English (US)' &&
            voice.name === 'Cassidy-English'
        );

        // If Cassidy-English is found, use it; otherwise find any English (US) voice
        const voicesForLanguage = sampleVoices?.data?.data?.filter(
          (voice: any) => voice.language === defaultLanguage
        );

        const defaultVoice =
          cassidyVoice ||
          voicesForLanguage?.find(
            (voice: any) => voice.labels?.gender === 'Female'
          ) ||
          voicesForLanguage?.[0];

        if (defaultVoice) {
          dispatch(
            updateAssistantDefinition({
              voice: {
                name: defaultVoice.name,
                _id: defaultVoice._id,
                voice_id: defaultVoice.voice_id,
                provider: 'Ultra',
                accent: defaultVoice.labels?.accent,
                gender: defaultVoice.labels?.gender,
              },
            })
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sampleVoices]);

  const getSectionStyle = (sectionId: string) => {
    if (selectedSubSection === sectionId) {
      document.getElementById(sectionId)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
    return {
      transform: selectedSubSection === sectionId ? 'scale(1.02)' : 'scale(1)',
      transition: 'transform 0.2s ease',
    };
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    dispatch(updateAssistantDefinition({ [name]: value }));
    if (errors[name]) {
      dispatch(setErrors({ ...errors, [name]: '' }));
    }
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    dispatch(updateAssistantDefinition({ [name]: value }));
    if (errors[name]) {
      dispatch(setErrors({ ...errors, [name]: '' }));
    }
  };

  const handleNext = async () => {
    try {
      await assistantDefinitionSchema.validate(assistantDefinition.data, {
        abortEarly: false,
      });
      dispatch(
        setStepStatus({
          step: 'assistantDefinition',
          status: { isComplete: true, hasError: false },
        })
      );
      dispatch(clearErrors());
      onNext();
    } catch (err) {
      if (err instanceof ValidationError) {
        const newErrors: Record<string, string> = {};
        err.inner.forEach(error => {
          newErrors[error.path!] = error.message;
        });
        dispatch(setErrors(newErrors));
        dispatch(
          setStepStatus({
            step: 'assistantDefinition',
            status: { isComplete: false, hasError: true },
          })
        );
      }
    }
  };

  // Helper function to get unique languages with their accents - Updated for Ultra voices
  const getUniqueLanguages = () => {
    if (!sampleVoices?.data?.data) return [];

    // Show all voices regardless of language
    const allVoices = sampleVoices.data.data.filter(
      (voice: any) => voice.language
    );

    // Use the full language string (e.g., "English (IN)", "Portuguese (PT)") as unique keys
    const languageSet = new Set<string>();
    allVoices.forEach((voice: any) => {
      if (voice.language) {
        languageSet.add(voice.language);
      }
    });

    return Array.from(languageSet).map(language => ({
      language,
      accents: [], // Not needed anymore as language already includes the accent
    }));
  };

  // Update the accent mapping to include "english"
  const getAvailableVoices = () => {
    if (!sampleVoices?.data?.data || !assistantDefinition.data.language)
      return [];

    const selectedLanguage = assistantDefinition.data.language;

    // Match voices based on exact language string (e.g., "English (IN)", "Portuguese (PT)")
    return sampleVoices?.data?.data?.filter(
      (voice: any) => voice.language === selectedLanguage
    );
  };

  const handlePrev = () => {
    if (isGeneralSupportFlow) {
      onPrev();
    } else {
      if (!shouldShowMenuConfigFlow) {
        navigate('/add-new-assistant');
      } else {
        onPrev();
      }
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: 'white',
      }}
    >
      <Box
        sx={{
          backgroundColor: 'white',
          px: 3,
          py: 2,
          gap: 2,
          width: '768px',
          mx: 'auto',
          height: 'calc(100vh - 100px)',
          overflowY: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {/* Header Section */}

        <div>
          <Typography
            sx={{ fontWeight: 600, fontSize: '18px', color: 'custom.textBlue' }}
          >
            Define Your AI Assistant
          </Typography>
          <Typography
            color="text.secondary"
            sx={{
              mb: 1,
              fontSize: '12px',
              fontWeight: 400,
              color: 'lightGray.text',
            }}
          >
            Give your AI assistant a unique identity and personality that
            matches the vibe of your restaurant.
          </Typography>
        </div>

        <Divider />

        {/* Assistant Name Section */}
        <Box
          id="assistant-name"
          sx={{
            ...getSectionStyle('assistant-name'),
            display: 'flex',
            gap: 1,
            py: 4,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '80%' }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontSize: '16px',
                fontWeight: 500,
                color: 'custom.textBlue',
              }}
            >
              Agent Name
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 1,
                fontSize: '12px',
                fontWeight: 400,
                color: 'lightGray.text',
              }}
            >
              Give your AI a name (e.g., "DineBot", "ChefMate")
            </Typography>
          </Box>
          <TextField
            fullWidth
            placeholder="ex: DineBot"
            name="assistantName"
            value={assistantDefinition.data.assistantName}
            onChange={handleChange}
            error={!!errors.assistantName}
            helperText={errors.assistantName}
            size="small"
            sx={{ bgcolor: 'white', height: '40px' }}
          />
        </Box>
        <Divider />

        {/* Assistant Behavior Section */}
        <Box
          id="behavior"
          sx={{
            ...getSectionStyle('behavior'),
            display: 'flex',
            gap: 1,
            flexDirection: 'column',
            py: 4,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontSize: '16px',
                fontWeight: 500,
                color: 'custom.textBlue',
              }}
            >
              Agent Behavior
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 2,
                fontSize: '12px',
                fontWeight: 400,
                color: 'lightGray.text',
              }}
            >
              Choose the AI assistant's interaction style that best suits your
              customers.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Personality
              </Typography>
              <TextField
                fullWidth
                size="small"
                name="personality"
                value={assistantDefinition.data.personality}
                onChange={handleChange}
                error={!!errors.personality}
                helperText={errors.personality}
                placeholder="ex: Professional"
                sx={{ bgcolor: 'white' }}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Language
              </Typography>
              <Select
                fullWidth
                name="language"
                value={assistantDefinition.data.language || ''}
                onChange={handleSelectChange}
                error={!!errors.language}
                size="small"
                sx={{
                  '& .MuiSelect-select': {
                    display: 'flex',
                    padding: '8px 12px',
                    alignItems: 'center',
                    borderRadius: '6px',
                    border: '1px solid #DCE0E7',
                    background: '#F8F9FC',
                    fontWeight: 400,
                    lineHeight: 'normal',
                    letterSpacing: '-0.14px',
                    color: theme.palette.lightGray.text,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                }}
              >
                {getUniqueLanguages().map(({ language }) => (
                  <MenuItem key={language} value={language}>
                    {language}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {assistantDefinition.data.agentChannel !== 'whatsapp' && (
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Voice
                </Typography>
                <Select
                  fullWidth
                  name="voice"
                  value={assistantDefinition.data.voice.name || ''}
                  onChange={(e: SelectChangeEvent<string>) => {
                    const selectedVoice = getAvailableVoices().find(
                      (voice: any) => voice.name === e.target.value
                    );
                    if (selectedVoice) {
                      dispatch(
                        updateAssistantDefinition({
                          voice: {
                            name: selectedVoice.name,
                            _id: selectedVoice._id,
                            voice_id: selectedVoice.voice_id, // Add voice_id for Ultra voices
                            provider: 'Ultra', // Set provider as Ultra
                            accent: selectedVoice.labels?.accent,
                            gender: selectedVoice.labels?.gender,
                          },
                        })
                      );
                    }
                  }}
                  error={!!errors.voice}
                  size="small"
                  sx={{
                    '& .MuiSelect-select': {
                      display: 'flex',
                      padding: '8px 12px',
                      alignItems: 'center',
                      borderRadius: '6px',
                      border: '1px solid #DCE0E7',
                      background: '#F8F9FC',
                      fontWeight: 400,
                      lineHeight: 'normal',
                      letterSpacing: '-0.14px',
                      color: theme.palette.lightGray.text,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                  }}
                  disabled={assistantDefinition.data.language === ''}
                >
                  {getAvailableVoices().map((voice: any) => (
                    <MenuItem
                      key={voice.voice_id || voice._id}
                      value={voice.name}
                    >
                      {voice.name} ({voice.labels?.gender})
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            )}
          </Box>
        </Box>
        <Divider />
        {/* Assistant Timezone Section */}
        <Box
          id="timezone"
          sx={{
            ...getSectionStyle('timezone'),
            display: 'flex',
            gap: 1,
            py: 4,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '16px',
                color: 'custom.textBlue',
              }}
            >
              Agent Timezone
            </Typography>
            <Typography
              color="text.secondary"
              sx={{
                fontSize: '12px',
                fontWeight: 400,
                color: 'lightGray.text',
              }}
            >
              Ensure the assistant operates at the right hours
            </Typography>
          </Box>
          <Select
            fullWidth
            name="timezone"
            value={assistantDefinition.data.timezone}
            onChange={handleSelectChange}
            error={!!errors.timezone}
            size="small"
            sx={{
              bgcolor: 'white',
              mb: 2,
              '& .MuiSelect-select': {
                display: 'flex',
                padding: '8px 12px',
                alignItems: 'center',
                borderRadius: '6px',
                border: '1px solid #DCE0E7',
                background: '#F8F9FC',
                fontWeight: 400,
                lineHeight: 'normal',
                letterSpacing: '-0.14px',
                color: theme.palette.lightGray.text,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
            }}
          >
            {timeZones.map((timezone: string) => (
              <MenuItem key={timezone} value={timezone}>
                {timezone}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          p: 1,
          borderTop: '1px solid',
          borderColor: 'lightGray.main',
          position: 'sticky',
          bottom: 0,
          bgcolor: 'background.paper',
        }}
      >
        <Button
          variant="outlined"
          startIcon={<CaretLeft size={16} />}
          onClick={handlePrev}
          sx={{
            height: '34px',
            border: '1px solid',
            borderColor: 'lightGray.main',
            borderRadius: '8px',
            color: 'custom.textBlue',
            backgroundColor: 'white',
            boxShadow: 'none',
            textTransform: 'none',
          }}
        >
          Prev
        </Button>
        <Button
          variant="contained"
          endIcon={<CaretRight size={16} />}
          onClick={handleNext}
          disabled={isPending}
          sx={{
            height: '34px',
            borderRadius: '8px',
            backgroundColor: 'primary.main',
            color: 'white',
            boxShadow: 'none',
            textTransform: 'none',
          }}
        >
          {isPending ? 'Saving...' : 'Next'}
        </Button>
      </Box>
    </Box>
  );
};

export default AssistantDefination;
